import { GridCellKind } from '@glideapps/glide-data-grid';

import type {
  CustomCell,
  GridCell,
  NumberCell,
  TextCell,
} from '@glideapps/glide-data-grid';

import type { Lang } from '../types';

import type { Row, ColumnId } from '../constants';

import { isMenuCell, getMenuCellTemplate } from '../../CustomCell/Menu';
import { isDateCell, getDateCellTemplate } from '../../CustomCell/Date';
import { isTickerCell, getTickerCellTemplate } from '../../CustomCell/Ticker';
import {
  isSharePriceCell,
  getSharePriceCellTemplate,
} from '../../CustomCell/SharePrice';
import { isSharesCell, getSharesCellTemplate } from '../../CustomCell/Shares';
import {
  isTotalCostCell,
  getTotalCostCellTemplate,
} from '../../CustomCell/TotalCost';

type MenuCell = ReturnType<typeof getMenuCellTemplate>;
type DateCell = ReturnType<typeof getDateCellTemplate>;
type TickerCell = ReturnType<typeof getTickerCellTemplate>;
type SharePriceCell = ReturnType<typeof getSharePriceCellTemplate>;
type SharesCell = ReturnType<typeof getSharesCellTemplate>;
type TotalCostCell = ReturnType<typeof getTotalCostCellTemplate>;

const resetCustomCell = (cell: CustomCell) => {
  if (isMenuCell(cell)) {
    return getMenuCellTemplate();
  }
  if (isDateCell(cell)) {
    return getDateCellTemplate({ lang: cell.data.lang });
  }
  if (isTickerCell(cell)) {
    return getTickerCellTemplate();
  }
  if (isSharePriceCell(cell)) {
    return getSharePriceCellTemplate();
  }
  if (isSharesCell(cell)) {
    return getSharesCellTemplate();
  }
  if (isTotalCostCell(cell)) {
    return getTotalCostCellTemplate();
  }
  return cell;
};

export const resetCell = (cell: GridCell) => {
  switch (cell.kind) {
    case GridCellKind.Text: {
      return {
        ...cell,
        data: '',
        displayData: '',
      };
    }
    case GridCellKind.Number: {
      return {
        ...cell,
        data: 0,
        displayData: '',
      };
    }
    case GridCellKind.Custom: {
      return resetCustomCell(cell);
    }
  }
  return cell;
};

const defaultCell: TextCell = {
  kind: GridCellKind.Text,
  allowOverlay: true,
  readonly: false,
  displayData: '',
  data: '',
};

export const createCell = (
  columnId?: ColumnId,
  rowData?: Row,
  lang?: Lang
):
  | MenuCell
  | DateCell
  | TickerCell
  | SharePriceCell
  | SharesCell
  | TotalCostCell
  | TextCell
  | NumberCell => {
  if (typeof columnId === 'undefined') return defaultCell;

  const data = rowData ?? { currencyISO: null };
  const value: unknown = data[columnId];

  const { currencyISO } = data;

  if (columnId === 'ticker') {
    return getTickerCellTemplate({ value, currencyISO });
  }

  if (columnId === 'date') {
    return getDateCellTemplate({ value, lang });
  }

  if (columnId === 'type') {
    return getMenuCellTemplate({ value });
  }

  if (columnId === 'price') {
    return getSharePriceCellTemplate({ value, currencyISO });
  }

  if (columnId === 'shares') {
    return getSharesCellTemplate({ value });
  }

  if (columnId === 'cost') {
    return getTotalCostCellTemplate({ value, currencyISO });
  }

  return defaultCell;
};
